/**
 * Custom hook encapsulating all grid-related state for the Forecast Summary page.
 *
 * Manages grid data, edit config, table/row labels, intake caches, index columns,
 * metrics, display controls, graph state, and all derived data (columnar, filtered,
 * column definitions). Also provides the applyResponse, cell-edit, and intake-toggle
 * handlers.
 */
import { useCallback, useMemo } from "react";
import { PER_SQM_SUFFIX } from "../../components/cubeDataTable/utils/metricConstants";
import type { CubePage } from "../useCubeSelectionState";
import { useFetchForecastMetaData } from "../useFetchForecastMetaData";
import type { ForecastSummaryData } from "../useFetchForecastSummaryData";
import { useSummaryDisplayControls } from "./useSummaryDisplayControls";
import { useSummaryGraphState } from "./useSummaryGraphState";
import { useSummaryGridData } from "./useSummaryGridData";
import { useSummaryIntakeState } from "./useSummaryIntakeState";

/** Options passed to useForecastSummaryGridState. */
interface UseForecastSummaryGridStateOptions {
  /** The page whose grid this is; its data and display controls are retained per page. */
  page: CubePage;
  /** The row dimension to use for column definitions (appliedFilters?.rowDim ?? rowDim). */
  appliedRowDim: string;
}

export function useForecastSummaryGridState({
  page,
  appliedRowDim
}: UseForecastSummaryGridStateOptions) {
  const { data: metaData } = useFetchForecastMetaData();
  const gridDataState = useSummaryGridData(page);
  const {
    gridData,
    setGridData,
    editConfig,
    tableLabels,
    metrics,
    editableAggs,
    hasIndexData
  } = gridDataState;

  const intake = useSummaryIntakeState({ page, ...gridDataState });

  const sortedMetrics = useMemo(() => {
    const sortOrder = metaData?.metricSortOrder;
    // Plain per-SQM metrics (`*_PER_SQM`) are evaluated entirely client-side, so
    // the backend no longer ships them in the response payload. Re-introduce
    // every per-SQM metric the metadata knows about — `perSqmValueGetter` then
    // computes each value from base / STORE_SQM, leaving cells blank when the
    // base column isn't present. `endsWith` excludes `*_PER_SQM_LOCATION_AVG`,
    // which the backend computes server-side and still ships with real values.
    const perSqmMetrics = sortOrder
      ? Object.keys(sortOrder).filter((m) => m.endsWith(PER_SQM_SUFFIX))
      : [];
    const allMetrics = [...new Set([...metrics, ...perSqmMetrics])];
    if (!sortOrder) return allMetrics;
    return allMetrics.sort(
      (a, b) => (sortOrder[a] ?? 999999) - (sortOrder[b] ?? 999999)
    );
  }, [metrics, metaData?.metricSortOrder]);

  const displayControls = useSummaryDisplayControls({
    page,
    sortedMetrics,
    editableAggs,
    editConfig,
    hasIndexData,
    appliedRowDim,
    metaData
  });

  const graphState = useSummaryGraphState({
    sortedMetrics,
    tableLabels,
    metaData
  });

  const { resetGraphSelection } = graphState;
  const applyResponse = useCallback(
    function applyResponse(response: ForecastSummaryData, markdownSign = 0) {
      const parsed = gridDataState.applyResponse(response, markdownSign);
      setGridData(parsed.gridData);
      intake.setIntakeCaches(parsed.intakeCaches);
      intake.setIntakeMode(response.main_intake_source);
      resetGraphSelection();
    },
    [gridDataState, setGridData, intake, resetGraphSelection]
  );

  return {
    gridData,
    tableLabels,
    filteredData: gridDataState.filteredData,
    filteredRowLabelsPerTable: gridDataState.filteredRowLabelsPerTable,
    columnDefs: displayControls.columnDefs,
    hasIndexData,
    indexError: gridDataState.indexError,
    metaData,
    category: displayControls.category,
    setCategory: displayControls.setCategory,
    agg: displayControls.agg,
    setAgg: displayControls.setAgg,
    planBy: displayControls.planBy,
    setPlanBy: displayControls.setPlanBy,
    visibility: displayControls.visibility,
    setVisibility: displayControls.setVisibility,
    setMetricVisible: displayControls.setMetricVisible,
    isFilterPending: displayControls.isFilterPending,
    intakeMode: intake.intakeMode,
    intakeCaches: intake.intakeCaches,
    availableIntakeSources: intake.availableIntakeSources,
    handleIntakeToggle: intake.handleIntakeToggle,
    graphVisible: graphState.graphVisible,
    setGraphVisible: graphState.setGraphVisible,
    graphMetric: graphState.graphMetric,
    setGraphMetric: graphState.setGraphMetric,
    graphTableIndex: graphState.graphTableIndex,
    setGraphTableIndex: graphState.setGraphTableIndex,
    graphMetricOptions: graphState.graphMetricOptions,
    graphTableOptions: graphState.graphTableOptions,
    savedAt: gridDataState.savedAt,
    setSavedAt: gridDataState.setSavedAt,
    hasUnsavedEdits: gridDataState.hasUnsavedEdits,
    setHasUnsavedEdits: gridDataState.setHasUnsavedEdits,
    applyResponse,
    clearLoadedData: gridDataState.clearLoadedData,
    resetIntake: intake.reset,
    handleCellValueChanged: gridDataState.handleCellValueChanged
  };
}
